import {
  CircularProgress,
  Container,
  Grid,
  Stack,
  styled,
} from '@mui/material';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderBreadcrumbs } from '@pflegenavi/web-components';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { SetupDirectDebit } from '../components/directDebit/SetupDirectDebit';
import { DirectDebitStatusCard } from '../components/directDebit/DirectDebitStatusCard';
import { CancelDirectDebit } from '../components/directDebit/CancelDirectDebit';
import {
  usePaymentInfoByResidentIdAsFamilyMember,
  useShowResidentForFamilyMemberPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  DirectDebitStatus,
  PaymentInitiatedBy,
  PendingChargePaymentStatus,
  ResidentState,
} from '@pflegenavi/shared/api';
import { DirectDebitInfoBox } from '../components/directDebit/DirectDebitInfoBox';
import { DirectDebitNonActiveCases } from '../components/directDebit/DirectDebitNonActiveCases';
import { useInitializeFM } from '../hooks/useInitializeFM';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface DirectDebitPageProps {}

export const DirectDebitPage: FC<DirectDebitPageProps> = () => {
  const { t } = useTranslation();
  const { selectedResident } = useInitializeFM();

  const { data: residentResultPhoenix, isPending: isResidentPending } =
    useShowResidentForFamilyMemberPhoenix(
      selectedResident?.residentId ?? '',
      selectedResident?.tenant ?? ''
    );

  const isResidentActive =
    residentResultPhoenix?.data.resident.state === ResidentState.active;

  const { data: paymentInfo, isPending: paymentInfoLoading } =
    usePaymentInfoByResidentIdAsFamilyMember(
      selectedResident?.residentId ?? ''
    );

  const directDebit = useMemo(() => {
    if (paymentInfoLoading) {
      return undefined;
    }
    if (paymentInfo?.initiatedBy === PaymentInitiatedBy.System) {
      return paymentInfo?.directDebit;
    }
    return DirectDebitStatus.Inactive;
  }, [paymentInfo, paymentInfoLoading]);

  const status = directDebit ?? DirectDebitStatus.Inactive;

  const paymentMethod = useMemo(() => {
    if (paymentInfo?.initiatedBy === PaymentInitiatedBy.System) {
      return paymentInfo?.paymentMethod;
    }
    return undefined;
  }, [paymentInfo]);

  // Check if there are any pending payments (supporting multiple payments)
  const pendingPayment =
    paymentInfo?.status.status === PendingChargePaymentStatus.Pending;

  // For multiple payments support, we consider any pending payment as blocking
  // This maintains the existing behavior while being compatible with multiple charges
  const hasPendingPayments = pendingPayment;

  const waitForPendingPaymentResult = paymentInfoLoading && !isResidentActive;

  const isPaymentDisabled =
    paymentInfo?.status.status === PendingChargePaymentStatus.Disabled;

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('fm-web-navigation.direct-debit')}
        links={[
          {
            name: t('fm-web-navigation.direct-debit'),
            href: fmAppMainPages.DIRECT_DEBIT,
          },
          { name: t('common.overview') },
        ]}
        sx={{
          pb: 0,
        }}
      />
      {(!directDebit ||
        paymentInfoLoading ||
        isResidentPending ||
        waitForPendingPaymentResult) && (
        <LoadingContainer alignItems="center">
          <CircularProgress variant="indeterminate" />
        </LoadingContainer>
      )}
      {directDebit && residentResultPhoenix && (
        <span>
          {!isResidentActive || isPaymentDisabled ? (
            <DirectDebitNonActiveCases
              residentState={residentResultPhoenix.data.resident.state}
              nursingHomePhone={
                residentResultPhoenix.data.resident.nursing_home.base_data
                  .phone ?? ''
              }
              pendingPayment={hasPendingPayments}
              paymentDisabled={isPaymentDisabled}
            />
          ) : (
            <>
              <DirectDebitInfoBox status={status} />
              <Grid
                container
                direction="row"
                spacing={3}
                sx={{
                  alignItems: 'flex-start',
                }}
              >
                <Grid
                  size={{
                    xs: 12,
                    md: 7,
                    lg: 8,
                  }}
                >
                  <SetupDirectDebit
                    status={status}
                    paymentMethod={paymentMethod}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 5,
                    lg: 4,
                  }}
                >
                  <DirectDebitStatusCard status={status} />
                  <CancelDirectDebit status={status} />
                </Grid>
              </Grid>
            </>
          )}
        </span>
      )}
    </Container>
  );
};

const LoadingContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    mt: 12,
  })
);
